<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="maxWidth"
    :fullscreen="fullscreen"
    :theme="activeVuetifyTheme"
    scrim="rgba(15, 27, 36, 0.35)"
    transition="dialog-bottom-transition"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div
      class="bm-glass bm-glass-lg bm-floating-dialog overflow-hidden"
      :class="activeTheme === 'dark' ? 'bm-floating-dialog--dark' : 'bm-floating-dialog--light'"
      :style="dragStyle"
    >
      <div
        class="bm-floating-dialog__header flex items-center justify-between px-5 py-4 cursor-move select-none"
        @pointerdown="startDrag"
      >
        <h3 class="bm-display-font text-lg font-semibold m-0">{{ title }}</h3>
        <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('update:modelValue', false)" />
      </div>

      <div class="bm-floating-dialog__body p-5 bm-scroll-thin overflow-y-auto" :style="{ maxHeight: fullscreen ? '82vh' : '70vh' }">
        <slot />
      </div>

      <div v-if="$slots.actions" class="bm-floating-dialog__actions flex justify-end gap-2 px-5 py-4">
        <slot name="actions" />
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
import {
  reactive, computed, ref, onBeforeUnmount, onMounted,
} from 'vue';
import { useUiStore } from '../../store/ui';
import { getVuetifyThemeName } from '../../theme/accentThemes';

const props = defineProps({
  modelValue: Boolean,
  title: { type: String, default: '' },
  maxWidth: { type: [String, Number], default: 640 },
  fullscreen: { type: Boolean, default: false },
});
defineEmits(['update:modelValue']);

const uiStore = useUiStore();
const offset = reactive({ x: 0, y: 0 });
const dragging = reactive({ active: false, startX: 0, startY: 0 });
const documentTheme = ref('');
let themeObserver;

function syncDocumentTheme() {
  if (typeof document === 'undefined') return;
  const app = document.querySelector('.v-application');
  if (app?.classList.contains('theme--dark')) {
    documentTheme.value = 'dark';
    return;
  }
  if (app?.classList.contains('theme--light')) {
    documentTheme.value = 'light';
    return;
  }
  documentTheme.value = '';
}

const activeTheme = computed(() => documentTheme.value || uiStore.theme || 'dark');
const activeVuetifyTheme = computed(() => getDialogThemeName(activeTheme.value));

function getDialogThemeName(theme) {
  return theme === 'dark'
    ? getVuetifyThemeName('dark', uiStore.themeAccent)
    : getVuetifyThemeName('light', uiStore.themeAccent);
}

const dragStyle = computed(() => (
  props.fullscreen ? {} : { transform: `translate(${offset.x}px, ${offset.y}px)` }
));

function startDrag(e) {
  if (props.fullscreen) return;
  dragging.active = true;
  dragging.startX = e.clientX - offset.x;
  dragging.startY = e.clientY - offset.y;
  window.addEventListener('pointermove', onDrag);
  window.addEventListener('pointerup', stopDrag);
}
function onDrag(e) {
  if (!dragging.active) return;
  offset.x = e.clientX - dragging.startX;
  offset.y = e.clientY - dragging.startY;
}
function stopDrag() {
  dragging.active = false;
  window.removeEventListener('pointermove', onDrag);
  window.removeEventListener('pointerup', stopDrag);
}

onMounted(() => {
  syncDocumentTheme();
  if (typeof MutationObserver === 'undefined') return;
  const target = document.querySelector('.v-application') || document.documentElement;
  themeObserver = new MutationObserver(syncDocumentTheme);
  themeObserver.observe(target, { attributes: true, attributeFilter: ['class'] });
});

onBeforeUnmount(() => {
  themeObserver?.disconnect();
  stopDrag();
});
</script>

<style scoped>
.bm-floating-dialog {
  --bm-floating-bg: rgba(222, 216, 201, 0.92);
  --bm-floating-panel: rgba(232, 227, 216, 0.84);
  --bm-floating-border: rgba(68, 86, 70, 0.32);
  --bm-floating-ink: rgba(24, 31, 26, 0.98);
  --bm-floating-muted: rgba(55, 68, 57, 0.86);
  --bm-floating-field: rgba(236, 233, 223, 0.92);
  --bm-floating-shadow: 0 28px 72px rgba(42, 49, 36, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.28);
  --bm-glass-bg: var(--bm-floating-bg);
  --bm-glass-border: var(--bm-floating-border);
  --bm-panel-solid: var(--bm-floating-panel);
  --bm-field-bg: var(--bm-floating-field);
  --bm-ink: var(--bm-floating-ink);
  --bm-ink-soft: var(--bm-floating-muted);
  --bm-text-strong: var(--bm-floating-ink);
  --bm-line-soft: rgba(61, 77, 62, 0.24);
  color: var(--bm-floating-ink);
  box-shadow: var(--bm-floating-shadow);
}

.bm-floating-dialog.bm-floating-dialog--dark {
  --bm-floating-bg: rgba(17, 31, 27, 0.94);
  --bm-floating-panel: rgba(20, 34, 29, 0.9);
  --bm-floating-border: rgba(226, 238, 218, 0.2);
  --bm-floating-ink: rgba(247, 242, 232, 0.96);
  --bm-floating-muted: rgba(207, 216, 203, 0.8);
  --bm-floating-field: rgba(9, 23, 20, 0.84);
  --bm-floating-shadow: 0 30px 80px rgba(0, 0, 0, 0.56), inset 0 1px 0 rgba(255, 255, 255, 0.12);
  --bm-line-soft: rgba(226, 238, 218, 0.14);
}

.bm-floating-dialog__header,
.bm-floating-dialog__actions {
  border-color: var(--bm-floating-border);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.18), transparent 52%),
    rgba(255, 255, 255, 0.04);
}

.bm-floating-dialog__header {
  border-bottom: 1px solid var(--bm-floating-border);
}

.bm-floating-dialog__actions {
  border-top: 1px solid var(--bm-floating-border);
}

.bm-floating-dialog__body {
  background:
    radial-gradient(circle at 12% 0%, rgba(167, 200, 162, 0.12), transparent 32%),
    radial-gradient(circle at 92% 18%, rgba(141, 200, 232, 0.1), transparent 30%);
}

.bm-floating-dialog :deep(.v-field) {
  background: var(--bm-floating-field) !important;
}

.bm-floating-dialog :deep(.v-alert) {
  border: 1px solid var(--bm-line-soft);
}
</style>
